import { LocationQueryValue } from 'vue-router';

import { emptyStrToUndefined } from './param';

import { postalCode } from '@/validations/regex';

/**
 * number型に変換する関数
 * @param number型に変換する値
 * @returns number型に変換された値
 */
export const toNumber = (value: unknown): number | undefined => {
  let parsedValue: number | undefined;

  if (value == null) return undefined;
  if (typeof value === 'number') parsedValue = value;
  else if (typeof value === 'string') parsedValue = parseFloat(value.replace(/,/g, ''));
  else parsedValue = undefined;

  return Number.isNaN(parsedValue) ? undefined : parsedValue;
};

/**
 * string型に変換する関数
 * @param string型に変換する値
 * @returns string型に変換された値
 */
export const toString = (value: unknown): string => {
  if (typeof value === 'string' || typeof value === 'number') {
    return value.toString();
  }
  return '';
};

/**
 * Dateに変換
 * @param value 変換する値
 * @returns 変換後の値（Date | undefined）
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const toDate = (value?: any): Date | undefined => {
  const typeString = typeof value;
  if (typeString === 'string' || typeString === 'number' || value instanceof Date) {
    const date = new Date(value);
    return date.toString() === 'Invalid Date' ? undefined : date;
  }
  return undefined;
};

/**
 * T[]に変換
 * @param value 変換するT型の値
 * @params {valuesForFiltering: フィルタリング用の配列}
 * @returns 変換後のT[]の値
 */
export const toArray = <T extends string = string>(
  value?: (string | null)[] | string | null,
  options?: { enableValues?: T[] },
): T[] => {
  const enableValues = options?.enableValues;

  if (!value) {
    return [];
  }

  if (typeof value === 'string') {
    // TODO: as使いたくない
    const filteredValue = !enableValues || (enableValues as string[]).includes(value)
      ? [value] as T[]
      : [];
    return [...new Set(filteredValue)];
  }
  // TODO: as使いたくない
  const filteredValue = value
    .filter((v): v is T => {
      if (!v) {
        return false;
      }
      return !enableValues || (enableValues as string[]).includes(v);
    });
  return [...new Set(filteredValue)];
};

/**
 * クエリパラメータの形式に変換
 * @param value 変換前の値
 * @returns 変換後の値
 */
export function toQueryParam(value?: Date): string | undefined;
export function toQueryParam(value?: string): string | undefined;
export function toQueryParam(value?: Date | string): string | undefined {
  if (typeof value === 'string') {
    return emptyStrToUndefined(value);
  }
  if (value instanceof Date) {
    return value?.toISOString();
  }

  return undefined;
}

/**
 * 数字をカンマ区切りに変換
 * @param num カンマ区切りにしたい数字
 * @returns 変換後の値
 */
export const toCommaNumber = (num?: number | null) => (num != null ? num.toLocaleString('jp-JP') : '');

/**
 * ハイフンのない郵便番号をハイフンありに変換(ハイフンありの場合はそのまま返却)
 * @param postalCode 郵便番号
 * @returns 変換後の値
 */
export const toPostalCodeWithHyphen = (input: string) => {
  if (!postalCode.test(input)) return undefined;
  const postalCodeWithoutHyphen = /^\d{7}$/;
  if (!postalCodeWithoutHyphen.test(input)) return input;
  return input.replace(/(\d{3})(\d{4})/, '$1-$2');
};

/**
 * string→boolean変換を行う関数
 * @param value 文字列のtrueまたはfalse
 * @returns booleanのtrueまたはfalse、paramがtrueまたはfalse以外の場合はundefined
 */
export const toBoolean = (value: LocationQueryValue | LocationQueryValue[]): boolean | undefined => {
  if (value === 'true') return true;
  if (value === 'false') return false;

  return undefined;
};

/**
 * boolean→string変換を行う関数
 * @param value booleanのtrueまたはfalse
 * @returns stringのtrueまたはfalse、paramがtrueまたはfalse以外の場合はundefined
 */
export const toBooleanString = (value: boolean | undefined): string | undefined => {
  if (value === true) return 'true';
  if (value === false) return 'false';

  return undefined;
};

/**
 * 端末IDを4−4−5桁に区切って表示する
 * @param input 変換前の端末
 * @returns 変換後の端末ID（13桁が仕様通り。不整合なデータはそのまま返す）
 */
export const toDisplayTerminalId = (input: string | undefined) => {
  if (input?.length !== 13) return input;
  return `${input.slice(0, 4)} ${input.slice(4, 8)} ${input.slice(8, 13)}`;
};

/**
 * 取引IDが20桁の場合、4−4−4-4-4桁に区切って表示する
 * @param input 変換前の取引ID
 * @returns 変換後の取引ID（20桁が仕様通り。不整合なデータはそのまま返す）
 */
export const toDisplayTransactionId = (input: string | undefined) => {
  if (input?.length !== 20) return input;
  return `${input.slice(0, 4)} ${input.slice(4, 8)} ${input.slice(8, 12)} ${input.slice(12, 16)} ${input.slice(16, 20)}`;
};

/**
 * カード番号をn桁に区切って表示する
 * @param input 変換前のカード番号
 * @param digit 区切り桁数（デフォルト：4）
 * @param separateChar 区切り文字（デフォルト：半角スペース）
 * @returns 変換後のカード番号（不整合なデータはそのまま返す）
 */

export const toDisplayCardNumber = (input: string | undefined, digit = 4, separateChar = ' ') => {
  if (!input) return input;
  const regexp = new RegExp(`(.{${digit}})`, 'g');
  return input.replace(regexp, `$1${separateChar}`).trim();
};

/**
 * 文字列をVersion文字列に変換する
 * @param input 変換前の文字列
 * @returns 変換後のVersion文字列
 */
export const toVersionText = (input: string | undefined) => {
  if (input == null) return undefined;
  return `Ver.${input}`;
};

/**
 * 文字列をマスキングする
 * @param value 変換前の値
 * @param digit 表示する桁数
 */
export const toMaskedText = (
  value: string | undefined,
  digit: number,
): string | undefined => {
  // 以下の場合、undefinedを返す
  // 1.null or undefinedの場合
  // 2.桁数がマイナスの場合
  // 3.digitがvalueの桁数を超える場合
  if (value == null || digit < 0 || digit > value.length) {
    return undefined;
  }

  if (digit === 0) {
    return value.replace(/./g, '*');
  }

  // 末尾からdigit桁目までの文字を表示し、それ以外はマスキングする
  return '*'.repeat(value.length - digit) + value.slice(-digit);
};
